import imageCompression from 'browser-image-compression';

export const EXPERT_AVATAR_DIMENSIONS = {
  width: 160,
  height: 160,
  aspectRatio: 1,
};

export const MAX_FILE_SIZE_KB = 500;

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

export function validateImageType(file: File): boolean {
  return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(file.type);
}

export function getFileTypeError(file: File): string {
  if (!validateImageType(file)) {
    return `Invalid file type: ${file.type}. Allowed types: JPEG, PNG, WEBP, GIF`;
  }
  return '';
}

export function getFileExtension(mimeType: string): string {
  const mapping: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
  };
  return mapping[mimeType] || 'jpg';
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

export async function getCroppedExpertAvatarImage(
  imageSrc: string,
  croppedAreaPixels: { x: number; y: number; width: number; height: number }
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  canvas.width = EXPERT_AVATAR_DIMENSIONS.width;
  canvas.height = EXPERT_AVATAR_DIMENSIONS.height;

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    EXPERT_AVATAR_DIMENSIONS.width,
    EXPERT_AVATAR_DIMENSIONS.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.95
    );
  });
}

export async function compressExpertAvatarIfNeeded(blob: Blob): Promise<Blob> {
  const fileSizeKB = blob.size / 1024;

  if (fileSizeKB <= MAX_FILE_SIZE_KB) {
    return blob;
  }

  const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });

  const compressedFile = await imageCompression(file, {
    maxSizeMB: MAX_FILE_SIZE_KB / 1024,
    maxWidthOrHeight: EXPERT_AVATAR_DIMENSIONS.width,
    useWebWorker: true,
    fileType: 'image/jpeg',
  });

  return compressedFile;
}

export async function uploadToS3WithRetry(
  uploadUrl: string,
  blob: Blob,
  maxRetries = 3
): Promise<void> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await uploadToS3(uploadUrl, blob);
      return;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Upload failed');

      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw new Error(
    `Failed to upload after ${maxRetries} attempts: ${lastError?.message}`
  );
}

export async function uploadToS3(uploadUrl: string, blob: Blob): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: {
      'Content-Type': 'image/jpeg',
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
}
